'use client';

import { useEffect, useLayoutEffect, useSyncExternalStore } from 'react';
import {
  BEST_BUY_PICKUP_CATALOGUE_ENABLED,
  BEST_BUY_PICKUP_ENABLED,
} from 'utils/featureFlags';
import { fetchBestBuyCatalogue } from './api';
import { BB_CATALOGUE_STORAGE_KEY, BB_CATALOGUE_TTL_MS } from './config';
import type { PickupCatalogueState } from './pickupCatalogueRule';

const IDLE: PickupCatalogueState = {
  status: 'idle',
  skus: null,
  variantIds: null,
  off: false,
  fetchedAt: 0,
};

let state: PickupCatalogueState = IDLE;
const listeners = new Set<() => void>();

const set = (next: PickupCatalogueState) => {
  state = next;
  listeners.forEach((fn) => fn());
};

/** Sync read for non-React callers (pickupSku). */
export const getPickupCatalogue = (): PickupCatalogueState => state;

/** The Shopify variant (bare number) of the Best Buy pickup product for an
 * ops SKU, or null when the catalogue is not loaded or lists none. */
export const pickupVariantIdFor = (opsSku: string): string | null =>
  state.variantIds?.get(opsSku) ?? null;

type StoredCatalogue = {
  fetchedAt: number;
  /** Absent in copies cached before the flag existed (read as true). */
  pickupAvailable?: boolean;
  skus: string[];
  variantIds: Record<string, string>;
};

const isStoredCatalogue = (value: unknown): value is StoredCatalogue => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.fetchedAt !== 'number' || !Number.isFinite(v.fetchedAt)) {
    return false;
  }
  if (!Array.isArray(v.skus) || !v.skus.every((s) => typeof s === 'string')) {
    return false;
  }
  if (
    v.pickupAvailable !== undefined &&
    typeof v.pickupAvailable !== 'boolean'
  ) {
    return false;
  }
  return (
    typeof v.variantIds === 'object' &&
    v.variantIds !== null &&
    !Array.isArray(v.variantIds) &&
    Object.values(v.variantIds).every((id) => typeof id === 'string')
  );
};

const readCached = (): StoredCatalogue | null => {
  try {
    const raw = window.sessionStorage.getItem(BB_CATALOGUE_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isStoredCatalogue(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeCached = (value: StoredCatalogue) => {
  try {
    window.sessionStorage.setItem(
      BB_CATALOGUE_STORAGE_KEY,
      JSON.stringify(value),
    );
  } catch {
    // Storage blocked or full: the in-memory copy still serves this page.
  }
};

const fromStored = (stored: StoredCatalogue): PickupCatalogueState => ({
  status: 'ready',
  skus: new Set(stored.skus),
  variantIds: new Map(Object.entries(stored.variantIds)),
  off: stored.pickupAvailable === false,
  fetchedAt: stored.fetchedAt,
});

const isFresh = (fetchedAt: number): boolean => {
  const age = Date.now() - fetchedAt;
  return age >= 0 && age < BB_CATALOGUE_TTL_MS;
};

/** Data older than this is dropped instead of served: a refresh that keeps
 * failing must not leave stale stock on screen. */
const MAX_STALE_MS = 2 * BB_CATALOGUE_TTL_MS;

const isTooStale = (fetchedAt: number): boolean =>
  Date.now() - fetchedAt > MAX_STALE_MS;

const enabled = (): boolean =>
  BEST_BUY_PICKUP_ENABLED &&
  BEST_BUY_PICKUP_CATALOGUE_ENABLED &&
  typeof window !== 'undefined';

let inFlight = false;

const fetchCatalogue = () => {
  if (inFlight) return;
  inFlight = true;
  const hadData = state.status === 'ready';
  // A refetch keeps serving the last good data, and a retry from 'error'
  // stays 'error' (no "Checking" flash every interval); only a first load
  // from 'idle' shows 'loading'.
  if (state.status === 'idle') set({ ...IDLE, status: 'loading' });
  // A failed refetch keeps the last good data until it is older than twice
  // the TTL; a failed first load is 'error'.
  const fail = () => {
    if (state.status === 'error') return;
    if (!hadData || isTooStale(state.fetchedAt)) {
      set({ ...IDLE, status: 'error' });
    }
  };
  fetchBestBuyCatalogue()
    .then((result) => {
      if (!result.ok) {
        fail();
        return;
      }
      const items = result.data.pickupAvailable ? result.data.items : [];
      const stored: StoredCatalogue = {
        fetchedAt: Date.now(),
        pickupAvailable: result.data.pickupAvailable,
        skus: items.map((item) => item.sku),
        variantIds: Object.fromEntries(
          items.flatMap((item) =>
            item.variantId ? [[item.sku, item.variantId]] : [],
          ),
        ),
      };
      writeCached(stored);
      set(fromStored(stored));
    })
    .catch(fail)
    .finally(() => {
      inFlight = false;
    });
};

/** Loads the catalogue from a fresh sessionStorage copy, else the network;
 * with ready data older than the TTL it refetches in the background. Never
 * throws: a 404 (endpoint not deployed), a bad body or a network error leaves
 * status 'error' (or keeps the last good data for up to twice the TTL), and
 * pickup stays off. Call it only from US-only components: other regions never
 * need the catalogue. No-op with the flag off. */
export const ensurePickupCatalogueLoaded = (): void => {
  if (!enabled()) return;
  if (state.status === 'ready' && isFresh(state.fetchedAt)) return;
  if (state.status === 'idle' || state.status === 'error') {
    const cached = readCached();
    // isFresh (under one TTL) is stricter than the 2x-TTL stale cap.
    if (cached && isFresh(cached.fetchedAt)) {
      set(fromStored(cached));
      return;
    }
  }
  // After a failed first load only the refresh paths (visibility, interval)
  // retry, never a re-render.
  fetchCatalogue();
};

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

// One shared refresh for every mounted consumer: a timer plus a refetch when
// the tab comes back, both only acting on stale data.
let consumers = 0;
let timer: ReturnType<typeof setInterval> | null = null;

const refreshIfStale = () => {
  if (document.visibilityState === 'hidden') return;
  if (state.status === 'ready' && isFresh(state.fetchedAt)) return;
  ensurePickupCatalogueLoaded();
};

const startRefresh = () => {
  consumers += 1;
  if (consumers > 1) return;
  document.addEventListener('visibilitychange', refreshIfStale);
  // The data landed a moment after the timer started, so the tick forces the
  // fetch instead of re-checking freshness (it would read "just under TTL").
  timer = setInterval(() => {
    if (enabled() && document.visibilityState !== 'hidden') fetchCatalogue();
  }, BB_CATALOGUE_TTL_MS);
};

const stopRefresh = () => {
  consumers -= 1;
  if (consumers > 0) return;
  document.removeEventListener('visibilitychange', refreshIfStale);
  if (timer) clearInterval(timer);
  timer = null;
};

// Layout effect on the client, so a valid sessionStorage copy renders 'ready'
// before the first paint; plain effect during SSR (no layout effects there).
const useIsoLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/** Subscribes to the catalogue (so callers re-render when it lands) and
 * triggers the load after mount, only when `enabled` (the US store): the
 * hosting components render on every region's buy page. While mounted and
 * enabled it also keeps the catalogue refreshed. */
export const usePickupCatalogue = (
  enabled: boolean,
): PickupCatalogueState['status'] => usePickupCatalogueState(enabled).status;

/** Like usePickupCatalogue, but returns the whole state (status and the
 * API's "pickup off" flag). */
export const usePickupCatalogueState = (
  enabled: boolean,
): PickupCatalogueState => {
  const current = useSyncExternalStore(
    subscribe,
    getPickupCatalogue,
    () => IDLE,
  );
  useIsoLayoutEffect(() => {
    if (!enabled) return;
    ensurePickupCatalogueLoaded();
    startRefresh();
    return stopRefresh;
  }, [enabled]);
  return current;
};
